import { createAction } from '@reduxjs/toolkit';
import { ContractDocSource } from './types';

export interface IContractDocWritePayload {
  placementId: number;
  templateText: string;
}

/** Create (add) candidate contract: HTML from editor; saga derives RTF for `TemplateText` on the API. */
export interface IContractDocCreatePayload {
  placementId: number;
  /** Backend may use int or string identifiers (e.g. AMIE Web). */
  documentId: number | string;
  templateHtml: string;
  isDraftManualEdit?: boolean;
  /** When set, post-processes generated RTF to restore `<<IMG:…>>` for pict blobs. */
  preservedApiTemplateTextRtf?: string;
}

/** Save draft: HTML from editor; saga derives RTF for `TemplateText` on the API. */
export interface IContractDocSaveDraftPayload {
  placementId: number;
  documentId: number;
  templateHtml: string;
  preservedApiTemplateTextRtf?: string;
}

/** Values passed to `placement/candidate-contract/publish` as `actionType` (backend contract). */
export type PlacementCandidateContractPublishActionType =
  | 'Publish'
  | 'Void'
  | 'OkToSend'
  | 'SentByTcc';

export interface IContractDocPublishPayload {
  placementId: number;
  contractId: string;
  /** When omitted, publish saga defaults to `Publish` (promote created contract). */
  actionType?: PlacementCandidateContractPublishActionType;
}

const getTemplates = createAction('GET_PLACEMENT_CANDIDATE_CONTRACT_TEMPLATES');
const getMergeTokensList = createAction('GET_PLACEMENT_CANDIDATE_CONTRACT_MERGE_TOKENS_LIST');
const generateDraft = createAction<{ placementId: number }>('GENERATE_PLACEMENT_CANDIDATE_CONTRACT_DRAFT');
const deleteContractDraft = createAction<{ placementId: number }>('DELETE_PLACEMENT_CANDIDATE_CONTRACT_DRAFT');

export interface IContractDocDraftDeleteNewPayload {
  placementId: number;
  documentId: number | string;
}

const deleteContractDraftNew = createAction<IContractDocDraftDeleteNewPayload>(
  'DELETE_PLACEMENT_CANDIDATE_CONTRACT_DRAFT_NEW',
);

const deleteContractDoc = createAction<{ placementId: number; contractId: string }>(
  'DELETE_PLACEMENT_CANDIDATE_CONTRACT_DOC',
);
const previewContractDoc = createAction<IContractDocWritePayload>('PREVIEW_PLACEMENT_CANDIDATE_CONTRACT_DOC');
const createContractDoc = createAction<IContractDocCreatePayload>('CREATE_PLACEMENT_CANDIDATE_CONTRACT_DOC');
const saveDraftTemplate = createAction<IContractDocSaveDraftPayload>('SAVE_PLACEMENT_CANDIDATE_CONTRACT_DRAFT');
const publishContractDoc = createAction<IContractDocPublishPayload>('PUBLISH_PLACEMENT_CANDIDATE_CONTRACT_DOC');
const voidContractDoc = createAction<IContractDocPublishPayload>('VOID_PLACEMENT_CANDIDATE_CONTRACT_DOC');

export interface IContractDocTscPublishPayload {
  placementId: number;
  documentId: number | string;
  shouldSetTsc: boolean;
}

const publishContractTscNew = createAction<IContractDocTscPublishPayload>(
  'PUBLISH_PLACEMENT_CANDIDATE_CONTRACT_TSC_NEW',
);
const getPdfDocument = createAction<{
  documentId: string;
  source: ContractDocSource;
  documentImageId?: string;
  /** `GET …/document/documentType/Contract/:documentId` instead of `placement/document/download`. */
  useContractDocumentTypeApi?: boolean;
}>('GET_PLACEMENT_CANDIDATE_CONTRACT_PDF_DOC');

/** Backend `placement/candidate-contract/status-update` — aligns with placement contract workflow status ids. */
export const placementCandidateContractStatusUpdateId = {
  requestContract: 2,
  sendByTcc: 3,
  okToSend: 4,
} as const;

export interface IContractDocStatusUpdatePayload {
  placementId: number;
  /** Contract row document id (numeric from API). */
  documentId: number | string;
  statusId: number;
}

const updateContractStatus = createAction<IContractDocStatusUpdatePayload>(
  'UPDATE_PLACEMENT_CANDIDATE_CONTRACT_STATUS',
);

const getContractCatalogTemplates = createAction('GET_PLACEMENT_CANDIDATE_CONTRACT_CATALOG_TEMPLATES');

const fetchContractTemplateRows = createAction<{
  placementId: number;
  contractId: number;
  templateId: number;
}>('FETCH_PLACEMENT_CONTRACT_TEMPLATE_ROWS');

export const serviceActions = {
  getTemplates,
  getMergeTokensList,
  generateDraft,
  deleteContractDraft,
  deleteContractDraftNew,
  deleteContractDoc,
  previewContractDoc,
  createContractDoc,
  saveDraftTemplate,
  publishContractDoc,
  voidContractDoc,
  publishContractTscNew,
  getPdfDocument,
  updateContractStatus,
  getContractCatalogTemplates,
  fetchContractTemplateRows,
};
